export const convertUnit = (value, fromUnit, toUnit, units) => {
  // If converting to the same unit, return the original value
  if (fromUnit === toUnit) return value;

  // Handle temperature conversions separately
  if (units[fromUnit]?.base || units[toUnit]?.base) {
    // Handle beaufort scale for wind speed
    if (units[fromUnit]?.base === 'beaufort' || units[toUnit]?.base === 'beaufort') {
      return convertBeaufort(value, fromUnit, toUnit, units);
    }
    // Handle all temperature conversions
    return convertTemperature(value, fromUnit, toUnit);
  }

  // Standard unit conversion using base values
  const fromBase = units[fromUnit];
  const toBase = units[toUnit];
  if (!fromBase || !toBase) return 0;

  // Convert to base unit, then to target unit
  const baseValue = value * fromBase;
  return baseValue / toBase;
};

const convertTemperature = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;

  // Convert to Celsius first
  let celsius;
  switch (fromUnit) {
    case 'Celsius (°C)': celsius = value; break;
    case 'Fahrenheit (°F)': celsius = (value - 32) * 5/9; break;
    case 'Kelvin (K)': celsius = value - 273.15; break;
    case 'Rankine (°R)': celsius = (value - 491.67) * 5/9; break;
    case 'Réaumur (°Ré)': celsius = value * 1.25; break;
    case 'Delisle (°De)': celsius = 100 - (value * 2/3); break;
    case 'Newton (°N)': celsius = value * 100/33; break;
    case 'Rømer (°Rø)': celsius = (value - 7.5) * 40/21; break;
    case 'Absolute Fahrenheit (°F abs)': celsius = ((value - 459.67) - 32) * 5/9; break;
    case 'Gas Mark (GM)': 
      // Gas mark conversion (approximate)
      if (value === 0) return 225; // Special case for Gas Mark 0
      celsius = (value * 14) + 121;
      break;
    default: return 0;
  }

  // Convert from Celsius to target unit
  switch (toUnit) {
    case 'Celsius (°C)': return celsius;
    case 'Fahrenheit (°F)': return (celsius * 9/5) + 32;
    case 'Kelvin (K)': return celsius + 273.15;
    case 'Rankine (°R)': return (celsius + 273.15) * 9/5;
    case 'Réaumur (°Ré)': return celsius * 0.8;
    case 'Delisle (°De)': return (100 - celsius) * 3/2;
    case 'Newton (°N)': return celsius * 33/100;
    case 'Rømer (°Rø)': return (celsius * 21/40) + 7.5;
    case 'Absolute Fahrenheit (°F abs)': return ((celsius * 9/5) + 32) + 459.67;
    case 'Gas Mark (GM)': 
      // Gas mark conversion (approximate)
      if (celsius < 135) return 0; // Special case for low temperatures
      return Math.round((celsius - 121) / 14);
    default: return 0;
  }
};

const convertBeaufort = (value, fromUnit, toUnit, units) => {
  // First convert any unit to m/s
  let metersPerSecond;
  if (fromUnit === 'Beaufort (Bft)') {
    // Convert Beaufort to m/s using the formula
    // Beaufort formula approximation: v = 0.836 * B^(3/2) m/s
    metersPerSecond = 0.836 * Math.pow(value, 3/2);
  } else {
    // Convert from source unit to m/s
    const fromBase = units[fromUnit];
    metersPerSecond = value * (typeof fromBase === 'object' ? 1 : fromBase);
  }

  // Convert from m/s to target unit
  if (toUnit === 'Beaufort (Bft)') {
    // Convert m/s to Beaufort scale
    // Beaufort formula: B = (v/0.836)^(2/3)
    const beaufort = Math.pow(metersPerSecond / 0.836, 2/3);
    // Round to nearest integer and clamp between 0 and 12
    return Math.min(12, Math.max(0, Math.round(beaufort)));
  } else {
    // Convert to target unit
    const toBase = units[toUnit];
    return metersPerSecond / (typeof toBase === 'object' ? 1 : toBase);
  }
};